import * as types from '../mutation-types'
import req from '../../lib/authRequest'
import dates from '../../lib/dates'
import logger from "../../lib/logger";

const state = {
  amount_due: 0.00,
  balance: 0.00,
  due_date: '',
  payment_amount: 0.00,
  overdue: false,
  payment_type: '',
  card_type: '',
  default_payment_account_id: '',
  temp_payment_id: '', // Used when people temporarily change payment account (i.e. one-time payment account)
  paya_url: '',
  payment_accounts: [],
  /* Expected
    {
      type: '',
      id: '',
      date: '',
      summary: '',
      amount: ''
    }
  */
  bills: [],
  billing_page: 0,
  payments: [],
  domains_payment: [],
  payment_processing: false,
  automatic: false,
  billing_address: {line1: '', line2: '', city: '', state: '', zip_code: '', fname: '', lname: '', mnami: '', formatted_address: ''}
}

const getters = {
  payment_type_opposite: state => {
    let temp = state.payment_type.toLowerCase() === 'manual' ? 'automatic' : 'manual'
    return temp.toUpperCase()
  },
  formatted_payment_type: state => {
    return state.payment_type.toUpperCase()
  },
  formatted_opposite_payment_type: state => {
    if (state.payment_type.toUpperCase() === 'MANUAL') {
      return 'AUTOMATIC'
    } else {
      return 'MANUAL'
    }
  },
  formatted_due_date: state => {
    return dates.dateFormat(state.due_date)
  },
  formatted_amount_due: state => {
    return parseFloat(state.amount_due).toFixed(2)
  },
  formatted_balance: state => {
    return parseFloat(state.balance).toFixed(2)
  },
  has_payment_accounts: state => {
    return state.payment_accounts.length !== 0;
  }
}

const mutations = {
  [types.SET_BILLING] (state, { amount_due, balance, due_date, overdue, automatic, default_payment_account_id }) {
    state.amount_due = amount_due
    state.balance = balance
    state.payment_amount = amount_due
    state.due_date = due_date
    state.overdue = overdue
    state.automatic = automatic
    if (automatic) {
      state.payment_type = 'AUTOMATIC'
    } else {
      state.payment_type = 'MANUAL'
    }
    state.default_payment_account_id = default_payment_account_id
    if (!state.temp_payment_id) {
      // only set this if it is unset since it is used for temporary changes
      state.temp_payment_id = default_payment_account_id;
    }
  },
  [types.SET_TEMP_PAYMENT_ID] (state, result) {
    state.temp_payment_id = result
  },
  [types.SET_PAYMENT_ACCOUNTS] (state, {records}) {
    // sort payment accounts first by default, then by card, then by expiration date, and lastly bank account by bank name
    records.sort((a, b) => {
      if (a.payment_account_id === state.default_payment_account_id) {
        return -1
      } else if (b.payment_account_id === state.default_payment_account_id) {
        return 1
      } else if (!a.is_bank_account && !b.is_bank_account) {
        return a.credit_card.expiration_date.localeCompare(b.credit_card.expiration_date);
      }
      else if (a.is_bank_account && b.is_bank_account) {
        return a.bank_account.bank_name.localeCompare(b.bank_account.bank_name)
      } else if (a.is_bank_account && !b.is_bank_account) {
        return 1
      } else {  // !a.is_bank_account && b.is_bank_account
        return -1
      }
    })
    state.payment_accounts = records
    if (state.payment_accounts.length === 1) {
      state.temp_payment_id = state.payment_accounts[0].payment_account_id;
    }
  },
  [types.SET_PAYA_URL] (state, url) {
    state.paya_url = url
  },
  [types.SET_AMOUNT_DUE] (state, amountDue) {
    state.amount_due = amountDue
  },
  [types.SET_DUE_DATE] (state, dueDate) {
    state.due_date = dueDate
  },
  [types.SET_OVERDUE] (state, overdue) {
    state.overdue = overdue
  },
  // eslint-disable-next-line camelcase
  [types.SET_BILLS] (state, {records}) {
    state.bills = records
    // eslint-disable-next-line camelcase
  },
    [types.SET_BILL_PAGE] (state, page) {
    state.billing_page = page;
    // eslint-disable-next-line camelcase
  },
  // eslint-disable-next-line camelcase
  [types.SET_PAYMENTS] (state, {records}) {
    state.payments = records
    // eslint-disable-next-line camelcase
  },
  [types.START_PAYMENT_REQUEST] (state) {
    state.payment_processing = true
  },
  [types.END_PAYMENT_REQUEST] (state) {
    state.payment_processing = false
  },
  [types.SET_BILLING_ADDRESS] (state, address) {
    state.billing_address = address;
  }
}

const actions = {
  getPaymentAccounts ({commit}, params) {
    return new Promise((resolve, reject) => {
      req.get({
        url: '/billing/payment_accounts',
        success (result) {
          for (let record of result.records) {
            record.is_bank_account = record.method === 'ACH' ? true : false;
            if (!record.is_bank_account){
              record.credit_card.exp_mm_yy = dates.iso_date_to_card_exp(record.credit_card.expiration_date, 2);
              record.credit_card.exp_mm_yyyy = dates.iso_date_to_card_exp(record.credit_card.expiration_date, 4);
            }
          }
          commit(types.SET_PAYMENT_ACCOUNTS, result)
          resolve()
        }
      })
    })
  },
  getPayaUrl ({commit}, account_name) {
    return new Promise((resolve, reject) => {
      req.get({
        url: '/billing/payment_account_url',
        params: {'account_name': account_name},
        success(result) {
          commit(types.SET_PAYA_URL, result.url);
          logger.debug('paya url result', result)
        }
      })
    })
  },
  addCard ({ commit, dispatch }, cardDetails) {
    return new Promise((resolve, reject) => {
      req.post({
        notifications: false,
        modalNotifications: true,
        url: '/billing/payment_accounts/card',
        data: cardDetails,
        success (result) {
          dispatch('getPaymentAccounts').then((response) => {
            dispatch('getInfo')
          })
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },
  addACH ({ commit, dispatch }, bankDetails) {
    return new Promise((resolve, reject) => {
      req.post({
        notifications: false,
        modalNotifications: true,
        url: '/billing/payment_accounts/ach',
        data: bankDetails,
        success (result) {
          dispatch('getPaymentAccounts').then((response) => {
            dispatch('getInfo')
          })
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },
  alterPaymentAccount ({ dispatch, commit }, input) {
    let payment_account_id = input.payment_account_id;
    delete input.payment_account_id;
    let notifications = true
    let modalNotifications = false;
    if ('notifications' in input) {
      notifications = input.notifications;
      delete input.notifications;
    }
    if ('modalNotifications' in input) {
      modalNotifications = input.modalNotifications;
      delete input.modalNotifications;
    }
    return new Promise((resolve, reject) => {
      req.put({
        url: `/billing/payment_accounts/${payment_account_id}`,
        data: input,
        notifications: notifications,
        modalNotifications: modalNotifications,
        success (result) {
          commit(types.SET_TEMP_PAYMENT_ID, '')  // reset the temp payment id so it is populated with the default payment account
          dispatch('getInfo')
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },
  removePaymentAccount ({ dispatch }, input) {
    let notifications = true
    let modalNotifications = false;
    if ('notifications' in input) {
      notifications = input.notifications;
    }
    if ('modalNotifications' in input) {
      modalNotifications = input.modalNotifications;
    }
    return new Promise((resolve, reject) => {
      req.delete({
        url: `/billing/payment_accounts/${input.payment_account_id}`,
        notifications: notifications,
        modalNotifications: modalNotifications,
        success (result) {
          dispatch('getPaymentAccounts').then((response) => {
            dispatch('getInfo')
          })
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  },
  getPayments ({ commit }, { page, limit }) {
    return new Promise((resolve, reject) => {
      req.get({
        url: '/billing/payments', // + page + '/' + limit,
        success (result) {
          if (result.length === 0) {
            reject()
            return
          }
          commit(types.SET_PAYMENTS, result)
          resolve()
        }
      })
    })
  },
  makePayment ({ getters, commit, dispatch }, paymentAmount) {
    let data = {
      amount: parseFloat(paymentAmount),
      payment_account_id: state.temp_payment_id
    }
    return new Promise((resolve, reject) => {
      req.post({
        url: '/billing/payments',
        data: data,
        success (result) {
          commit(types.END_PAYMENT_REQUEST)
          resolve()
        },
        error (result) {
          commit(types.END_PAYMENT_REQUEST)
          reject(result)
        }
      })
    })
  },
  getBills ({ commit }, { page, limit }) {
    return new Promise((resolve, reject) => {
      req.get({
        url: '/billing/bills', // + page + '/' + limit,
        success (result) {
          if (result.length === 0) {
            reject()
            return
          }
          commit(types.SET_BILLS, result)
          resolve()
        }
      })
    })
  },
  updateBillingAddress({ dispatch }, address) {
    return new Promise((resolve, reject) => {
      req.post({
        url: '/billing/address',
        data: address,
        notifications: true,
        modalNotifications: false,
        success (result) {
          dispatch('getInfo')
          resolve(result)
        },
        error (result) {
          reject(result)
        }
      })
    })
  }
}

export default {
  state,
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/modules/billing.js